import React from 'react'
import { useParams } from 'react-router-dom'
import { Box, Chip, Typography, Alert, CircularProgress } from '@mui/material'
import { useAPI } from 'contexts/APIProvider'
import { useSnackBarAlert } from 'contexts/SnackBarAlertProvider'
import { AvailabilityCheckResultSearchForm } from 'types/interfaces'
import Header from 'components/Header'
import AdminAvailabilityCheckResultTable from 'components/AdminAvailabilityCheckResultTable'
import DescriptionList from 'components/DescriptionList'
import AvailabilityCheck from 'logic/AvailabilityCheck'
import update from 'immutability-helper'

const AdminAvailabilityCheckDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const { api } = useAPI()
    const { showAlert } = useSnackBarAlert()
    const [availabilityCheck, setAvailabilityCheck] = React.useState<AvailabilityCheck | null>(null)
    const [loading, setLoading] = React.useState<boolean>(true)
    const [error, setError] = React.useState<string | null>(null)
    const [form, setForm] = React.useState<AvailabilityCheckResultSearchForm>({})

    const fetchAvailabilityCheck = React.useCallback(async () => {
        if (!id) return
        
        try {
            setLoading(true)
            setError(null)
            const check = await api.getAvailabilityCheck(Number(id), ['num_results'])
            setAvailabilityCheck(check)
            // Set the availability_check_id in the form for the results table
            setForm(prev => update(prev, { availability_check_id: { $set: Number(id) } }))
        } catch (error) {
            setError('Failed to fetch availability check')
            showAlert('error', 'Failed to fetch availability check')
            console.error(error)
        } finally {
            setLoading(false)
        }
    }, [api, id, showAlert])

    const updateForm = React.useCallback((name: string, value: any) => {
        setForm(update(form, { [name]: { $set: value } }))
    }, [form])

    React.useEffect(() => {
        fetchAvailabilityCheck()
    }, [fetchAvailabilityCheck])

    const jobDateLabel = React.useMemo(() => {
        const startAt = availabilityCheck?.start_at.setZone('Europe/London')
        return `${startAt?.toFormat('ccc, LLL dd, yyyy')}`
    }, [availabilityCheck])

    const jobTimeLabel = React.useMemo(() => {
        const startAt = availabilityCheck?.start_at.setZone('Europe/London')
        const endAt = availabilityCheck?.end_at.setZone('Europe/London')
        return `${startAt?.toFormat('h:mm a')} - ${endAt?.toFormat('h:mm a')}`
    }, [availabilityCheck])

    const createdAtLabel = React.useMemo(() => {
        return availabilityCheck?.created_at.setZone('Europe/London').toFormat('ccc, LLL dd, yyyy, h:mm a') ?? 'N/A'
    }, [availabilityCheck])

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress />
            </Box>
        )
    }

    if (error || !availabilityCheck) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
                <Header text='Availability Check Detail' />
                <Alert severity='error'>{error || 'Availability check not found'}</Alert>
            </Box>
        )
    }

    const descriptionData = [
        { label: 'ID', value: availabilityCheck.id },
        { label: 'Status', value: (
            <Chip
                size='small'
                label={availabilityCheck.is_expired ? 'Expired' : 'Active'}
                color={availabilityCheck.is_expired ? 'error' : 'success'}
            />
        )},
        { label: 'Job Date', value: (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography>{jobDateLabel}</Typography>
                <Typography variant="caption">{jobTimeLabel}</Typography>
            </Box>
        )},
        { label: 'Lunch Break', value: `${availabilityCheck.lunch_break.toFormat('mm')} minutes` },
        { label: 'Practice Name', value: availabilityCheck.practice_name },
        { label: 'Role', value: availabilityCheck.role },
        { label: 'Payment Option', value: availabilityCheck.payment_option },
        { label: 'Settlement Days', value: `${availabilityCheck.settlement_days} days` },
        { label: 'Location', value: (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography>{availabilityCheck.postcode}</Typography>
                <Typography variant="caption">({availabilityCheck.lat.toFixed(4)}, {availabilityCheck.lng.toFixed(4)})</Typography>
            </Box>
        )},
        { label: 'Requirements', value: (() => {
            const requirements = []
            if (availabilityCheck.sedation_required) requirements.push('Sedation')
            if (availabilityCheck.radiography_required) requirements.push('Radiography')
            if (availabilityCheck.implant_required) requirements.push('Implant')
            if (availabilityCheck.orthodontics_required) requirements.push('Orthodontics')
            
            if (requirements.length === 0) {
                return 'None'
            }
            
            return (
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1, flexWrap: 'wrap' }}>
                    {requirements.map(requirement => (
                        <Chip key={requirement} size='small' label={requirement} color='primary' />
                    ))}
                </Box>
            )
        })()},
        { label: 'Results', value: (
            <Box sx={{ display: 'flex', flexDirection: 'row', gap: 1 }}>
                <Chip size='small' label={`${availabilityCheck.num_available} Available`} color='success' />
                <Chip size='small' label={`${availabilityCheck.num_unavailable} Unavailable`} color='error' />
                <Chip size='small' label={`${availabilityCheck.num_pending} Pending`} color='warning' />
            </Box>
        )},
        { label: 'Created At', value: createdAtLabel },
    ]

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, height: '100%' }}>
            <Header text={`Availability Check #${availabilityCheck.id}`} />
            
            <Box sx={{ display: 'flex', flexDirection: ['column', 'row'], gap: 2, flex: 1, minHeight: 0 }}>
                {/* Left side - Availability Check Details */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1, 
                    width: ['100%', '30%'],
                    minWidth: ['auto', 300]
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Check Details
                    </Typography>
                    <Box sx={{ 
                        border: 1, 
                        borderColor: 'divider', 
                        borderRadius: 1, 
                        p: 2,
                        bgcolor: 'background.paper',
                        flex: 1,
                        overflow: 'auto'
                    }}>
                        <DescriptionList 
                            data={descriptionData}
                            labelWidth="40%"
                            valueWidth="60%"
                            columnGap={2}
                            rowGap={1}
                        />
                    </Box>
                </Box>

                {/* Right side - Results Table */}
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    gap: 1, 
                    width: ['100%', '70%'],
                    flex: 1,
                    minHeight: 0
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        Nurse Responses
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: 0 }}>
                        <AdminAvailabilityCheckResultTable
                            form={form}
                            updateForm={updateForm}
                        />
                    </Box>
                </Box>
            </Box>
        </Box>
    )
}

export default AdminAvailabilityCheckDetail
